import React, { memo, useState, useEffect, useCallback } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Box, Typography, Paper, Chip, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Router as OltIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { oltService, OLTCard } from '../../../services/oltService';
import OlTCardPortForm from '../../forms/OlTCardPortForm';
import { useDiagramInteractive } from '../../../utils/useDiagramInteractive';

interface PortWithSlot {
    id: number;
    portNumber: string;
    sfp: string;
    status: 'active' | 'inactive' | 'maintenance';
    onuCount: number;
    maxOnus: number;
    slot: number;
    txpower?: number;
    description?: string;
}

interface OLTCardWithPorts extends OLTCard {
    ports: PortWithSlot[];
}

interface OLTNodeData {
    id: number;
    olt_id: number;
    oltName: string;
    oltVendor: string;
    oltModel: string;
    onDelete?: (id: number) => void;
}

const statusColor: Record<string, string> = {
    active: '#4caf50',
    inactive: '#9e9e9e',
    maintenance: '#ff9800',
};

const HANDLE_PX = 12;
const HANDLE_OFFSET = -8;

const handleBaseStyle = {
    width: HANDLE_PX,
    height: HANDLE_PX,
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    flexShrink: 0,
} as const;

const OLTNode = ({ data, selected }: NodeProps<OLTNodeData>) => {
    const isDiagramInteractive = useDiagramInteractive();
    const [cardsWithPorts, setCardsWithPorts] = useState<OLTCardWithPorts[]>([]);
    const [loading, setLoading] = useState(true);
    const [editPortOpen, setEditPortOpen] = useState(false);
    const [editPortData, setEditPortData] = useState<{ port: PortWithSlot; card: OLTCardWithPorts } | null>(null);

    const loadCardsAndPorts = useCallback(async () => {
        try {
            setLoading(true);
            const res = await oltService.getById(data.olt_id);
            const oltData = res || (res as any).data;
            const ports = (oltData as any).pon_ports || (res as any).data?.pon_ports || [];

            const cardsMap: Record<number, OLTCardWithPorts> = {};
            ports.forEach((port: any) => {
                const slotNum = port.slot || 1;
                if (!cardsMap[slotNum]) {
                    cardsMap[slotNum] = {
                        id: slotNum,
                        olt_id: data.olt_id,
                        slot: slotNum,
                        qtde_portas: 0,
                        status: 'active',
                        ativo: true,
                        ports: []
                    };
                }
                cardsMap[slotNum].ports.push({ ...port, slot: slotNum });
            });

            const cardsComplete = Object.values(cardsMap);
            cardsComplete.sort((a, b) => a.slot - b.slot);
            cardsComplete.forEach(card => {
                card.ports.sort((a, b) => (parseInt(a.portNumber) || 0) - (parseInt(b.portNumber) || 0));
            });
            setCardsWithPorts(cardsComplete);
        } catch (error) {
            console.error('Erro ao carregar portas da OLT:', error);
        } finally {
            setLoading(false);
        }
    }, [data.olt_id]);

    useEffect(() => {
        loadCardsAndPorts();
    }, [loadCardsAndPorts]);

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (data.onDelete) data.onDelete(data.id);
    };

    const handleEditPort = (e: React.MouseEvent, port: PortWithSlot, card: OLTCardWithPorts) => {
        e.stopPropagation();
        setEditPortData({ port, card });
        setEditPortOpen(true);
    };

    const handleEditPortClose = () => {
        setEditPortOpen(false);
        setEditPortData(null);
    };

    // Calcular total de portas para a altura
    const totalPorts = cardsWithPorts.reduce((sum, card) => sum + card.ports.length, 0);
    const nodeHeight = Math.max(120, 60 + totalPorts * 28 + cardsWithPorts.length * 30);

    // Contar portas para gerar IDs de handle únicos
    let globalPortIndex = 0;

    return (
    <>
        <Paper
            elevation={selected ? 8 : 3}
            sx={{
                minWidth: 280,
                borderRadius: 2,
                overflow: 'visible',
                border: selected ? '2px solid #2196f3' : '1px solid #e0e0e0',
                transition: 'all 0.2s ease',
            }}
        >

            {/* Header */}
            <Box sx={{
                px: 1.5, py: 1,
                background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <OltIcon sx={{ fontSize: 18 }} />
                    <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                        {data.oltName}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                        label={data.oltVendor}
                        size="small"
                        sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                    />
                    {data.onDelete && isDiagramInteractive && (
                        <IconButton size="small" onClick={handleDelete} sx={{ color: 'white', p: 0.3 }}>
                            <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Body com cards e portas */}
            <Box sx={{ px: 1, py: 0.5, bgcolor: '#fafafa', minHeight: 40 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={20} />
                    </Box>
                ) : cardsWithPorts.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ py: 1, display: 'block', textAlign: 'center' }}>
                        Nenhum chassi encontrado
                    </Typography>
                ) : (
                    cardsWithPorts.map((card) => (
                        <Box key={card.id} sx={{ mb: 0.5 }}>
                            {/* Título do Chassi */}
                            <Box sx={{
                                px: 1, py: 0.3,
                                bgcolor: '#e3f2fd',
                                borderRadius: 1,
                                borderLeft: '3px solid #1565c0',
                                mb: 0.3,
                            }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.65rem', color: '#1565c0' }}>
                                    Chassi {card.slot} {card.model ? `(${card.model})` : ''}
                                </Typography>
                            </Box>

                            {/* Portas empilhadas à direita */}
                            {card.ports.map((port) => {
                                const portHandleId = `port-${port.id}`;
                                globalPortIndex++;

                                return (
                                    <Box
                                        key={port.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-end',
                                            py: 0.2,
                                            px: 0.5,
                                            position: 'relative',
                                            '&:hover': { bgcolor: '#f5f5f5' },
                                        }}
                                    >
                                        <Tooltip title={`Porta ${port.portNumber} | ONUs: ${port.onuCount}/${port.maxOnus} | SFP: ${port.sfp || 'N/A'}`} placement="left">
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                width: '100%',
                                                justifyContent: 'space-between',
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {port.txpower !== undefined && port.txpower !== null && (
                                                        <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#1976d2', bgcolor: '#e3f2fd', px: 0.5, py: 0.1, borderRadius: 1 }}>
                                                            {Number(port.txpower).toFixed(2)} dBm
                                                        </Typography>
                                                    )}
                                                    {isDiagramInteractive && (
                                                    <Tooltip title="Editar porta">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(ev) => handleEditPort(ev, port, card)}
                                                            sx={{ p: 0.15, color: '#1976d2' }}
                                                        >
                                                            <EditIcon sx={{ fontSize: 12 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    )}
                                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666' }}>{port.description}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                                    <Box sx={{
                                                        width: 6, height: 6, borderRadius: '50%',
                                                        bgcolor: statusColor[port.status] || '#9e9e9e',
                                                    }} />
                                                    <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500 }}>
                                                        PON {port.portNumber}
                                                    </Typography>                                                    
                                                </Box>
                                            </Box>
                                        </Tooltip>

                                        {/* Handle de saída por porta (direita) */}
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                right: HANDLE_OFFSET,
                                                transform: 'translateY(-50%)',
                                                width: HANDLE_PX,
                                                height: HANDLE_PX,
                                                zIndex: 2,
                                            }}
                                        >
                                            <Handle
                                                type="source"
                                                position={Position.Right}
                                                id={portHandleId}
                                                style={{
                                                    ...handleBaseStyle,
                                                    background: statusColor[port.status] || '#9e9e9e',
                                                    border: '2px solid #fff',
                                                    position: 'absolute',
                                                    left: 0,
                                                    top: 0,
                                                    transform: 'none',
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    ))
                )}
            </Box>

            {/* Footer */}
            <Box sx={{
                px: 1, py: 0.3,
                bgcolor: '#e8eaf6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#666' }}>
                    {data.oltModel}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#666' }}>
                    {totalPorts} porta{totalPorts !== 1 ? 's' : ''}
                </Typography>
            </Box>
        </Paper>

        {/* Modal de edição de porta */}
        {editPortData && (
            <OlTCardPortForm
                open={editPortOpen}
                onClose={handleEditPortClose}
                onSuccess={() => {
                    loadCardsAndPorts();
                    handleEditPortClose();
                }}
                oltId={String(data.olt_id)}
                cardSlot={editPortData.card.slot}
                initialData={{
                    id: editPortData.port.id,
                    portNumber: editPortData.port.portNumber,
                    maxOnus: editPortData.port.maxOnus,
                    txpower: editPortData.port.txpower,
                    status: editPortData.port.status,
                    description: editPortData.port.description,
                }}
            />
        )}
    </>
    );
};

export default memo(OLTNode);
